// 어드민 대시보드와 노동부(gov) 대시보드가 함께 쓰는 HUD 화면.
// 개인 식별 정보를 받지 않도록 model은 집계값과 익명 피드만 담는다.
import React, { useEffect, useMemo, useState } from 'react';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { motion } from 'framer-motion';
import {
  ArrowTrendingUpIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import '../../styles/admin-dashboard-gov.css';
import '../../styles/admin-dashboard-cyber.css';
import { HUD_LABELS_EN } from './hudLabels';

export const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
export const NUMBER = new Intl.NumberFormat('en-US');


const interpolate = (value, vars) =>
  vars ? value.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] ?? '')) : value;

const defaultT = (key, vars) => interpolate(HUD_LABELS_EN[key] ?? key, vars);

const CHART_TOOLTIP = {
  backgroundColor: 'rgba(3, 10, 22, 0.96)',
  border: '1px solid rgba(34, 211, 238, 0.35)',
  borderRadius: '2px',
  color: '#e8f7ff',
  fontSize: '12px',
  boxShadow: '0 0 24px rgba(34, 211, 238, 0.18)',
};
const AXIS_TICK = { fontSize: 10, fill: '#5a7a90' };
const AXIS_LINE = { stroke: 'rgba(34,211,238,0.2)' };
const GRID_STROKE = 'rgba(34,211,238,0.12)';

const PERIODS = [
  { key: '7', label: '7D', title: '7 Days' },
  { key: '30', label: '30D', title: '30 Days' },
  { key: '90', label: '90D', title: '90 Days' },
  { key: 'YTD', label: 'YTD', title: 'Year to Date' },
];

const formatHudTime = (timeZone) =>
  new Date().toLocaleTimeString('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
const formatHudDate = (timeZone) =>
  new Date().toLocaleDateString('en-US', { timeZone, weekday: 'short', month: 'short', day: 'numeric' });
const hudClockNow = () => ({
  seoulTime: formatHudTime('Asia/Seoul'),
  seoulDate: formatHudDate('Asia/Seoul'),
  yangonTime: formatHudTime('Asia/Yangon'),
  yangonDate: formatHudDate('Asia/Yangon'),
});

const EMPTY_STAGE = { count: 0, amountUSD: 0 };

const Counter = ({ value, formatter = (v) => v }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = display;
    const to = Number(value) || 0;
    const step = (now) => {
      const p = Math.min(1, (now - start) / 600);
      setDisplay(from + (to - from) * (1 - (1 - p) ** 3));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>{formatter(display)}</>;
};

/**
 * model:
 *  kpis: { totalRemit, todayRemit, todayRemitDelta, totalUsers }
 *  compliance: { averageCompliance, compliant, total }
 *  stages: { stage1..stage4: { count, amountUSD } }  (Hecto / 수취은행 / 완료 / 실패)
 *  totalRemittances, online, syncing, lastUpdate
 *  remittanceTrend: [{ date, amount }], signupTrend: [{ date, users }]
 *  statusMix: { processingUSD, completedUSD, failedUSD }
 *  feed: [{ key, at, corridor, amountUSD, state: 'cleared'|'failed'|'processing', status }]
 */
const CommandHud = ({
  model,
  t = defaultT,
  compact = false,
  range,
  onRangeChange,
  logoSrc,
  headerActions = null,
  notice = null,
  footer = null,
  style,
}) => {
  const [hudClock, setHudClock] = useState(hudClockNow);

  useEffect(() => {
    const id = window.setInterval(() => setHudClock(hudClockNow()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const kpis = model.kpis || {};
  const compliance = model.compliance || {};
  const stages = {
    stage1: model.stages?.stage1 || EMPTY_STAGE,
    stage2: model.stages?.stage2 || EMPTY_STAGE,
    stage3: model.stages?.stage3 || EMPTY_STAGE,
    stage4: model.stages?.stage4 || EMPTY_STAGE,
  };
  const totalRemittances = model.totalRemittances || 0;
  const online = !!model.online;
  const syncing = !!model.syncing;
  const averageCompliance = Number(compliance.averageCompliance) || 0;
  const todayRemitDelta = Number(kpis.todayRemitDelta) || 0;

  const inProgressCount = stages.stage1.count + stages.stage2.count;
  const successRate =
    totalRemittances > 0 ? ((stages.stage3.count / totalRemittances) * 100).toFixed(1) : '0.0';

  const lastUpdateLabel = (model.lastUpdate ? new Date(model.lastUpdate) : new Date()).toLocaleString('en-US', {
    timeZone: 'Asia/Yangon',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const opsRadar = useMemo(() => {
    const success = Number(successRate) || 0;
    const totalRemit = kpis.totalRemit || 0;
    const todayShare = totalRemit > 0
      ? Math.min(100, ((kpis.todayRemit || 0) / Math.max(totalRemit / 90, 1)) * 100)
      : 0;
    const load = Math.min(100, (inProgressCount / Math.max(totalRemittances || 1, 1)) * 100 + 20);
    return [
      { metric: 'Liquidity', value: Math.min(100, Math.round(success)) },
      { metric: 'AML/CFT', value: Math.min(100, Math.round(averageCompliance)) },
      { metric: 'Throughput', value: Math.min(100, Math.round(todayShare || success * 0.8)) },
      { metric: 'Uptime', value: online ? 98 : 62 },
      { metric: 'Welfare', value: Math.min(100, Math.round(((compliance.compliant || 0) / Math.max(compliance.total || 1, 1)) * 100)) },
      { metric: 'Latency', value: Math.max(20, Math.round(100 - load)) },
    ];
  }, [successRate, kpis.totalRemit, kpis.todayRemit, inProgressCount, totalRemittances, averageCompliance, online, compliance.compliant, compliance.total]);

  const opsChartHeight = compact ? 200 : 240;
  const chartHeight = compact ? 160 : 200;

  const feedRows = useMemo(() => (model.feed || []).slice(0, compact ? 4 : undefined).map((item, idx) => {
    const ts = item.at ? new Date(item.at).getTime() : 0;
    const time = Number.isFinite(ts) && ts > 0
      ? new Date(ts).toLocaleTimeString('en-GB', { timeZone: 'Asia/Yangon', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
      : '--:--:--';
    let badge = 'proc';
    let label = item.status || 'PROCESSING';
    if (item.state === 'cleared') {
      badge = 'ok';
      label = 'CLEARED';
    } else if (item.state === 'failed') {
      badge = 'fail';
      label = 'FAILED';
    }
    return {
      key: item.key || `${idx}-${time}`,
      time,
      corridor: item.corridor || 'KR-SEOUL → MM-YGN',
      amount: Number(item.amountUSD) || 0,
      badge,
      label,
    };
  }), [model.feed, compact]);

  const statusMixData = [
    { name: t('processing'), value: model.statusMix?.processingUSD || 0, color: '#22d3ee' },
    { name: t('completed'), value: model.statusMix?.completedUSD || 0, color: '#4ade80' },
    { name: t('failed'), value: model.statusMix?.failedUSD || 0, color: '#f472b6' },
  ];
  const pipelineLoadData = [
    { name: t('stageHecto'), value: stages.stage1.count, color: '#22d3ee' },
    { name: t('bank'), value: stages.stage2.count, color: '#a78bfa' },
    { name: t('done'), value: stages.stage3.count, color: '#4ade80' },
    { name: t('failShort'), value: stages.stage4.count, color: '#f472b6' },
  ];

  const processStageCards = [
    { stage: 'stage1', step: '01', title: t('stageHecto'), badge: t('badgeProcessing'), badgeClass: 'processing', barClass: 'info' },
    { stage: 'stage2', step: '02', title: t('stageBank'), badge: t('badgePayout'), badgeClass: 'uab', barClass: 'info' },
    { stage: 'stage3', step: '03', title: t('stageDone'), badge: t('badgeDone'), badgeClass: 'done', barClass: 'done' },
    { stage: 'stage4', step: '04', title: t('stageFail'), badge: t('badgeAlert'), badgeClass: 'fail', barClass: 'fail' },
  ];

  const tickerItems = [
    `LIVE LINK · ${online ? 'ONLINE' : 'DEGRADED'}`,
    `SUCCESS ${successRate}%`,
    `IN-FLIGHT ${inProgressCount}`,
    `TODAY ${CURRENCY.format(kpis.todayRemit || 0)}`,
    `USERS ${NUMBER.format(kpis.totalUsers || 0)}`,
    `COMPLIANCE ${averageCompliance.toFixed(1)}%`,
    `SYNC ${lastUpdateLabel}`,
    stages.stage4.count > 0 ? `ALERT FAILED ${stages.stage4.count}` : 'THREAT WINDOW CLEAR',
  ];

  return (
    <div className="admin-dash-gov" style={style}>
      <div className="adg-shell">
        <motion.header
          className="adg-header"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div>
            <div className="adg-gold-line" />
            <div className="adg-brand">
              <img src={logoSrc} alt="Ministry of Labour" />
              <div>
                <h1>{t('title')}</h1>
                <p>{t('subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="adg-clocks">
            <div className="adg-clock seoul">
              <div className="zone">Seoul · KST</div>
              <div className="time">{hudClock.seoulTime}</div>
              <div className="date">{hudClock.seoulDate}</div>
            </div>
            <div className="adg-clock yangon">
              <div className="zone">Yangon · MMT</div>
              <div className="time">{hudClock.yangonTime}</div>
              <div className="date">{hudClock.yangonDate}</div>
            </div>
          </div>

          <div className="adg-header-meta">
            <span className={`adg-chip ${online ? 'live' : 'offline'}`}>
              <span className="dot" />
              {online ? t('live') : t('offline')}
            </span>
            {syncing && (
              <span className="adg-chip live">
                <span className="dot" />
                {t('syncing')}
              </span>
            )}
            {stages.stage4.count > 0 && (
              <span className="adg-chip alert">{t('fail', { n: stages.stage4.count })}</span>
            )}
            <div className="adg-period">
              <span>{t('period')}</span>
              {PERIODS.map((period) => (
                <button
                  key={period.key}
                  type="button"
                  className={range === period.key ? 'active' : ''}
                  onClick={() => onRangeChange?.(period.key)}
                  title={period.title}
                >
                  {period.label}
                </button>
              ))}
            </div>
            {headerActions}
          </div>
        </motion.header>

        {notice}

        <motion.section
          className="adg-kpi-grid"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
        >
          <article className="adg-kpi tone-blue">
            <div className="adg-kpi-top">
              <div>
                <div className="adg-kpi-label">{t('totalVolume')}</div>
                <div className="adg-kpi-sub">{t('totalVolumeSub')}</div>
              </div>
              <div className="adg-kpi-icon">
                <CurrencyDollarIcon className="w-5 h-5" />
              </div>
            </div>
            <div className="adg-kpi-value">
              <Counter value={kpis.totalRemit} formatter={(v) => CURRENCY.format(v)} />
            </div>
            <div className="adg-kpi-foot">
              <span className="adg-pill up">
                <ArrowTrendingUpIcon className="w-3 h-3" />
                {t('ledger')}
              </span>
              <span className="adg-pill">{t('allTime')}</span>
            </div>
          </article>

          <article className="adg-kpi tone-cyan">
            <div className="adg-kpi-top">
              <div>
                <div className="adg-kpi-label">{t('today')}</div>
                <div className="adg-kpi-sub">{t('todaySub')}</div>
              </div>
              <div className="adg-kpi-icon">
                <ClockIcon className="w-5 h-5" />
              </div>
            </div>
            <div className="adg-kpi-value">
              <Counter value={kpis.todayRemit || 0} formatter={(v) => CURRENCY.format(v)} />
            </div>
            <div className="adg-kpi-foot">
              <span className={`adg-pill ${todayRemitDelta >= 0 ? 'up' : 'down'}`}>
                {todayRemitDelta >= 0 ? <ArrowUpIcon className="w-3 h-3" /> : <ArrowDownIcon className="w-3 h-3" />}
                {CURRENCY.format(Math.abs(todayRemitDelta))}
              </span>
              <span className="adg-pill">{t('vsYesterday')}</span>
            </div>
          </article>

          <article className="adg-kpi tone-emerald">
            <div className="adg-kpi-top">
              <div>
                <div className="adg-kpi-label">{t('citizens')}</div>
                <div className="adg-kpi-sub">{t('citizensSub')}</div>
              </div>
              <div className="adg-kpi-icon">
                <UsersIcon className="w-5 h-5" />
              </div>
            </div>
            <div className="adg-kpi-value">
              <Counter value={kpis.totalUsers} formatter={(v) => NUMBER.format(Math.round(v))} />
            </div>
            <div className="adg-kpi-foot">
              <span className="adg-pill up">
                <ArrowTrendingUpIcon className="w-3 h-3" />
                {t('active')}
              </span>
              <span className="adg-pill">{t('workers')}</span>
            </div>
          </article>

          <article className="adg-kpi tone-amber">
            <div className="adg-kpi-top">
              <div>
                <div className="adg-kpi-label">{t('compliance')}</div>
                <div className="adg-kpi-sub">{t('complianceSub')}</div>
              </div>
              <div className="adg-kpi-icon">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
            </div>
            <div className="adg-kpi-value">
              <Counter value={averageCompliance} formatter={(v) => `${v.toFixed(2)}%`} />
            </div>
            <div className="adg-kpi-foot">
              <span className="adg-pill">
                {compliance.compliant || 0}/{compliance.total || 0}
              </span>
              <span className="adg-pill">{t('cleared')}</span>
            </div>
          </article>
        </motion.section>

        <motion.section
          className="adg-panel adg-panel--compact"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.28 }}
        >
          <div className="adg-panel-head adg-panel-head--compact">
            <div className="adg-panel-title adg-panel-title--compact">
              <div>
                <h2>{t('pipeline')}</h2>
                <p>{t('pipelineSub')}</p>
              </div>
            </div>
            <div className="adg-pipe-meta">
              <span className={`adg-chip ${online ? 'live' : 'offline'}`}>
                <span className="dot" />
                {online ? t('liveShort') : t('offline')}
              </span>
              {syncing && <span className="adg-chip">{t('syncing')}…</span>}
            </div>
          </div>

          <div className="adg-panel-body adg-panel-body--compact">
            <div className="adg-pipeline" role="list">
              {processStageCards.map(({ stage, step, title, badge, badgeClass, barClass }, index) => (
                <div key={stage} className="adg-pipe-step" role="listitem">
                  {index > 0 && <span className="adg-pipe-rule" aria-hidden="true" />}
                  <div className="adg-pipe-card">
                    <div className="adg-pipe-card-top">
                      <span className="adg-pipe-stepno">{step}</span>
                      <span className={`adg-stage-badge ${badgeClass}`}>{badge}</span>
                    </div>
                    <div className="adg-pipe-title">{title}</div>
                    <div className="adg-pipe-nums">
                      <strong>{stages[stage].count}</strong>
                      <span>{CURRENCY.format(stages[stage].amountUSD || 0)}</span>
                    </div>
                    <div className={`adg-stage-bar ${barClass}`}>
                      <i
                        style={{
                          width: `${Math.min(100, (stages[stage].count / Math.max(1, totalRemittances)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!compact && (
              <div className="adg-pipe-stats">
                <div className="adg-pipe-stat">
                  <span className="k">{t('inProgress')}</span>
                  <span className="v">{inProgressCount}</span>
                </div>
                <div className="adg-pipe-stat">
                  <span className="k">{t('completed')}</span>
                  <span className="v">{stages.stage3.count}</span>
                  <span className="s">{CURRENCY.format(stages.stage3.amountUSD || 0)}</span>
                </div>
                <div className="adg-pipe-stat">
                  <span className="k">{t('attention')}</span>
                  <span className={`v ${stages.stage4.count > 0 ? 'warn' : ''}`}>{stages.stage4.count}</span>
                </div>
                <div className="adg-pipe-stat">
                  <span className="k">{t('success')}</span>
                  <span className="v">{successRate}%</span>
                </div>
              </div>
            )}
          </div>
        </motion.section>

        <motion.section
          className="adg-ops-grid"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
        >
          <div className="adg-chart-card">
            <div className="adg-chart-head">
              <div className="adg-chart-icon">
                <CurrencyDollarIcon className="w-5 h-5" />
              </div>
              <div>
                <h3>{t('throughput')}</h3>
                <p>{t('throughputSub')}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={opsChartHeight}>
              <AreaChart data={model.remittanceTrend || []} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                <defs>
                  <linearGradient id="colorCyberRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.55} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} opacity={1} />
                <XAxis dataKey="date" tick={AXIS_TICK} axisLine={AXIS_LINE} />
                <YAxis tickFormatter={(v) => CURRENCY.format(v)} tick={AXIS_TICK} width={60} axisLine={AXIS_LINE} />
                <Tooltip formatter={(v) => CURRENCY.format(v)} contentStyle={CHART_TOOLTIP} />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#22d3ee"
                  strokeWidth={2.5}
                  fill="url(#colorCyberRevenue)"
                  name={t('volume')}
                  dot={{ r: 2.5, fill: '#67e8f9', stroke: '#0891b2' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="adg-chart-card">
            <div className="adg-chart-head">
              <div className="adg-chart-icon">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
              <div>
                <h3>{t('risk')}</h3>
                <p>{t('riskSub')}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={opsChartHeight}>
              <RadarChart data={opsRadar} cx="50%" cy="50%" outerRadius="70%">
                <PolarGrid stroke="rgba(34,211,238,0.22)" />
                <PolarAngleAxis dataKey="metric" tick={{ fill: '#8fb4c9', fontSize: 10 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                <Radar name={t('score')} dataKey="value" stroke="#22d3ee" fill="#22d3ee" fillOpacity={0.28} strokeWidth={2} />
                <Tooltip contentStyle={CHART_TOOLTIP} />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div className="adg-chart-card adg-feed">
            <div className="adg-chart-head">
              <div className="adg-chart-icon">
                <ArrowTrendingUpIcon className="w-5 h-5" />
              </div>
              <div>
                <h3>{t('feed')}</h3>
                <p>{t('feedSub')}</p>
              </div>
            </div>
            <div className="adg-telemetry" style={{ marginBottom: 10 }}>
              <div className={`adg-tele-row ${online ? 'ok' : ''}`}>
                <span className="k">{t('gateway')}</span>
                <span className="v">{online ? 'ONLINE' : 'DEGRADED'}</span>
              </div>
              <div className={`adg-tele-row ${Number(successRate) >= 95 ? 'ok' : ''}`}>
                <span className="k">{t('clearance')}</span>
                <span className="v">{successRate}%</span>
              </div>
            </div>
            <ul className="adg-feed-list">
              {feedRows.length === 0 && (
                <li className="adg-feed-item">
                  <span className="t">--:--:--</span>
                  <span className="c">{t('awaiting')}</span>
                  <span className="adg-feed-status proc">IDLE</span>
                </li>
              )}
              {feedRows.map((row) => (
                <li key={row.key} className="adg-feed-item">
                  <span className="t">{row.time}</span>
                  <span className="c">
                    {row.corridor} · <span className="a">{CURRENCY.format(row.amount)}</span>
                  </span>
                  <span className={`adg-feed-status ${row.badge === 'ok' ? '' : row.badge}`}>{row.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        <motion.section
          className="adg-charts"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24 }}
        >
          <div className="adg-chart-card">
            <div className="adg-chart-head">
              <div className="adg-chart-icon">
                <ArrowTrendingUpIcon className="w-5 h-5" />
              </div>
              <div>
                <h3>{t('statusMix')}</h3>
                <p>{t('statusMixSub')}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={statusMixData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} opacity={1} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#5a7a90' }} axisLine={AXIS_LINE} />
                <YAxis tickFormatter={(v) => CURRENCY.format(v)} tick={AXIS_TICK} width={60} axisLine={AXIS_LINE} />
                <Tooltip formatter={(v) => CURRENCY.format(Number(v))} contentStyle={CHART_TOOLTIP} />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {statusMixData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="adg-chart-card">
            <div className="adg-chart-head">
              <div className="adg-chart-icon">
                <UsersIcon className="w-5 h-5" />
              </div>
              <div>
                <h3>{t('enrollment')}</h3>
                <p>{t('enrollmentSub')}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={model.signupTrend || []} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} opacity={1} />
                <XAxis dataKey="date" tick={AXIS_TICK} axisLine={AXIS_LINE} />
                <YAxis tickFormatter={(v) => NUMBER.format(v)} tick={AXIS_TICK} width={50} axisLine={AXIS_LINE} />
                <Tooltip formatter={(v) => NUMBER.format(v)} contentStyle={CHART_TOOLTIP} />
                <Bar dataKey="users" fill="#38bdf8" name={t('newUsers')} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="adg-chart-card">
            <div className="adg-chart-head">
              <div className="adg-chart-icon">
                <ClockIcon className="w-5 h-5" />
              </div>
              <div>
                <h3>{t('load')}</h3>
                <p>{t('loadSub')}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={pipelineLoadData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} opacity={1} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#5a7a90' }} axisLine={AXIS_LINE} />
                <YAxis tick={AXIS_TICK} width={40} axisLine={AXIS_LINE} allowDecimals={false} />
                <Tooltip contentStyle={CHART_TOOLTIP} />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {pipelineLoadData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        <div className="adg-ticker" aria-hidden="true">
          <div className="adg-ticker-track">
            {[...tickerItems, ...tickerItems].map((item, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`${item}-${idx}`}>{item}</span>
            ))}
          </div>
        </div>

        {footer}
      </div>
    </div>
  );
};

export default CommandHud;
